/*
 * Decléor — Image scroll reveal
 * ---------------------------------------------------------------------------
 * Drives the curtain / greyscale / zoom reveal on the image block of
 * sections/section-double.liquid. All the motion lives in CSS
 * (snippets/image-reveal-style.liquid); this file does one thing — decide when an element is
 * "in" and when it is "out", and put `is-in` / `is-out` on it.
 *
 * WHY AN IntersectionObserver AND NOT THE THEME'S AOS.
 * The house rule is that reveals ride the theme's AOS (data-aos="hero" + data-aos-anchor +
 * data-aos-order), and every other custom block here follows it. AOS cannot serve this one: it
 * fires once, forwards, and has no notion of an element leaving again. The block ships an exit
 * effect, so the trigger has to be two-way. The rest of the contract is kept anyway — the
 * merchant's reduced-motion promise is honoured in CSS, and nothing here animates a property AOS
 * also touches.
 *
 * THE THRESHOLD IS CAPPED BY WHAT THE ELEMENT CAN ACTUALLY REACH.
 * "Trigger point: 40%" means 40% of the image visible. An image taller than the viewport can
 * never BE 40% visible — intersectionRatio tops out at viewportHeight / elementHeight — so a naive
 * threshold arms an observer that never fires and leaves the image behind a closed curtain for
 * good. The full-height image blocks in this theme (the height classes go to 100vh) hit this on
 * the first try. The threshold is therefore clamped to 90% of the reachable maximum, and
 * recomputed on theme:resize, because that maximum moves with the viewport.
 *
 * EXIT MODES
 *   hold    — reveal once, then stop observing. Cheapest, and the right default for a hero.
 *   reverse — leaving plays the curtain closed again, stagger reversed.
 *   replay  — leaving snaps back with no transition (CSS .dc-rv--reset), re-entry plays in full.
 * The two exit modes only fire at intersectionRatio 0, i.e. once the element is COMPLETELY off
 * screen. Closing a curtain that is still half on screen reads as a glitch, not as an exit.
 *
 * ES5 on purpose: every custom script in assets/ is a classic deferred IIFE with no build step.
 * No arrow functions, no const/let, no optional chaining.
 *
 * No-op without a [data-dc-reveal]. Sets window.__dcImageReveal so the inline guard in
 * image-reveal-style.liquid knows the stylesheet is safe to keep.
 */
(function () {
  'use strict';

  window.__dcImageReveal = true;

  var BOUND = 'data-dc-rv-bound';
  var items = [];

  function num(el, attr, fallback) {
    var raw = parseFloat(el.getAttribute(attr));
    return Number.isNaN(raw) ? fallback : raw;
  }

  function reveal(el) {
    el.classList.remove('is-out');
    el.classList.add('is-in');
  }

  function conceal(el) {
    el.classList.remove('is-in');
    el.classList.add('is-out');
  }

  /* The highest intersectionRatio this element can reach in this viewport, less a margin.
     A threshold at or above it would never fire. */
  function reachable(el, wanted) {
    var height = el.offsetHeight;
    var max;
    if (!height) {
      return wanted;
    }
    max = Math.min(1, window.innerHeight / height) * 0.9;
    return wanted > max ? max : wanted;
  }

  function arm(item) {
    var threshold = reachable(item.el, item.wanted);
    var observer;

    if (item.observer) {
      item.observer.disconnect();
    }

    observer = new IntersectionObserver(
      function (records) {
        var i;
        var record;
        for (i = 0; i < records.length; i += 1) {
          record = records[i];
          if (record.isIntersecting && record.intersectionRatio >= threshold) {
            reveal(item.el);
            if (item.mode === 'hold') {
              observer.disconnect();
              item.observer = null;
            }
          } else if (item.mode !== 'hold' && record.intersectionRatio === 0) {
            conceal(item.el);
          }
        }
      },
      {
        threshold: [0, threshold],
        /* A positive offset grows the observation box past the fold so the reveal arms earlier;
           a negative one waits until the image has climbed further up the screen. */
        rootMargin: item.offset + '% 0px ' + item.offset + '% 0px',
      }
    );

    observer.observe(item.el);
    item.observer = observer;
  }

  function bind(el) {
    var wanted;
    var item;

    if (el.hasAttribute(BOUND)) {
      return;
    }
    el.setAttribute(BOUND, '');

    /* No IntersectionObserver: show the image. Never leave a curtain closed because a feature
       is missing. */
    if (!('IntersectionObserver' in window)) {
      reveal(el);
      return;
    }

    wanted = num(el, 'data-dc-rv-threshold', 25) / 100;
    if (wanted > 0.99) {
      wanted = 0.99;
    }

    item = {
      el: el,
      mode: el.getAttribute('data-dc-rv-exit') || 'hold',
      wanted: wanted,
      offset: num(el, 'data-dc-rv-offset', 0),
      observer: null,
    };

    items.push(item);
    arm(item);
  }

  function boot(scope) {
    var root = document;
    var nodes;
    var i;

    if (scope && scope.querySelectorAll) {
      root = scope;
    }

    nodes = root.querySelectorAll('[data-dc-reveal]');
    for (i = 0; i < nodes.length; i += 1) {
      bind(nodes[i]);
    }
  }

  function rearm() {
    var i;
    for (i = 0; i < items.length; i += 1) {
      if (items[i].observer) {
        arm(items[i]);
      }
    }
  }

  boot(document);

  /* Theme editor: a settings change re-renders the section, so the old nodes are gone and the new
     ones carry no BOUND flag. Re-binding the section is enough — the observers of the removed
     nodes go with them. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  /* Selecting the block in the editor should show the merchant the finished state, not a curtain
     they have to scroll to open. */
  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target.querySelector('[data-dc-reveal]');
    if (el) {
      reveal(el);
    }
  });

  document.addEventListener('theme:resize', rearm);
})();
